import { useMemo, useState } from 'react';
import {
  useAdminAuditLog,
  useAdminUsers,
  type AdminAuditLogEntry,
} from '../../lib/api/queries';
import { Th, Td } from './components';

/**
 * /admin/log — every successful platform-admin mutation appears here, most
 * recent first. Designed for the "who did what when" investigation rather
 * than as a real-time activity feed (the underlying query staleness is 15s).
 */

const ACTIONS = [
  'tenant.created',  'tenant.renamed',  'tenant.deleted', 'tenant.restored',
  'user.created',    'user.updated',    'user.disabled',  'user.enabled', 'user.deleted',
];

export function AdminAuditLogPage() {
  const [actor,      setActor]      = useState('');
  const [action,     setAction]     = useState('');
  const [targetType, setTargetType] = useState('');
  const [from,       setFrom]       = useState('');
  const [to,         setTo]         = useState('');

  const filter = useMemo(() => ({
    actorUserId: actor      || undefined,
    action:      action     || undefined,
    targetType:  targetType || undefined,
    fromUtc:     from       ? new Date(from).toISOString() : undefined,
    toUtc:       to         ? new Date(to + 'T23:59:59').toISOString() : undefined,
  }), [actor, action, targetType, from, to]);

  const entries = useAdminAuditLog(filter);
  const users   = useAdminUsers();

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Audit log</h1>
        <p className="text-sm text-gray-500">
          Every successful action on the admin surface. Append-only — entries
          are never edited or deleted.
        </p>
      </div>

      <section className="bg-white border border-gray-200 rounded-lg p-3
                          grid grid-cols-2 md:grid-cols-5 gap-3">
        <Filter label="Actor">
          <select value={actor} onChange={e => setActor(e.target.value)}
                  className={selectCls}>
            <option value="">Any</option>
            {users.data?.map(u => (
              <option key={u.userId} value={u.userId}>{u.displayName}</option>
            ))}
          </select>
        </Filter>
        <Filter label="Action">
          <select value={action} onChange={e => setAction(e.target.value)}
                  className={selectCls}>
            <option value="">Any</option>
            {ACTIONS.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </Filter>
        <Filter label="Target type">
          <select value={targetType} onChange={e => setTargetType(e.target.value)}
                  className={selectCls}>
            <option value="">Any</option>
            <option value="tenant">tenant</option>
            <option value="user">user</option>
          </select>
        </Filter>
        <Filter label="From">
          <input type="date" value={from}
                 onChange={e => setFrom(e.target.value)}
                 className={selectCls} />
        </Filter>
        <Filter label="To">
          <input type="date" value={to}
                 onChange={e => setTo(e.target.value)}
                 className={selectCls} />
        </Filter>
      </section>

      <EntryTable entries={entries.data ?? []} loading={entries.isLoading} />
    </div>
  );
}

function EntryTable({
  entries, loading,
}: { entries: AdminAuditLogEntry[]; loading: boolean }) {
  if (loading)         return <p className="text-sm text-gray-500">Loading log…</p>;
  if (!entries.length) return <p className="text-sm text-gray-500">No entries match.</p>;

  return (
    <section className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
          <tr>
            <Th>When</Th>
            <Th>Actor</Th>
            <Th>Action</Th>
            <Th>Summary</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {entries.map(e => (
            <tr key={e.id} className="hover:bg-gray-50">
              <Td>
                <span className="text-xs text-gray-500 tabular-nums">
                  {new Date(e.occurredAtUtc).toLocaleString()}
                </span>
              </Td>
              <Td>
                <span className="text-gray-700">{e.actorEmail}</span>
              </Td>
              <Td>
                <ActionChip action={e.action} />
              </Td>
              <Td>{e.summary}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function ActionChip({ action }: { action: string }) {
  // Colour-code by action family so the eye can scan groups at a glance.
  // Reds for destructive, greens for create/restore, blue for everything else.
  const destructive = /\.(deleted|disabled)$/.test(action);
  const constructive = /\.(created|restored|enabled)$/.test(action);
  const cls = destructive
    ? 'bg-danger-500/10 text-danger-500 border-danger-500/30'
    : constructive
      ? 'bg-success-50 text-success-600 border-success-500/30'
      : 'bg-gray-100 text-gray-700 border-gray-300';
  return (
    <span className={'inline-flex items-center text-[11px] font-medium px-2 py-0.5 ' +
                     'rounded-full border font-mono ' + cls}>
      {action}
    </span>
  );
}

function Filter({
  label, children,
}: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-600 mb-1">{label}</span>
      {children}
    </label>
  );
}

const selectCls =
  'w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm bg-white ' +
  'focus:outline-none focus:ring-2 focus:ring-accent-alt-500 focus:border-transparent';
